import { linhaDoProduto } from './linhaDoProduto';

/**
 * As listas dos seletores da Busca Avançada.
 *
 * ## O incidente
 *
 * Até 12/10/2026, abrir a Busca Avançada na tela de chamados pedia **todos os
 * chamados já criados** — 266 mil em produção, com as relações completas — só
 * para montar os seletores de Categoria, Contato, Produto e Representante. O
 * processo do PHP morria, o Apache devolvia a própria página de 500, e sem PHP
 * vivo não saíam cabeçalhos de CORS: o console mostrava um erro de CORS que não
 * tinha nada a ver com CORS.
 *
 * O `catch` do modal só fazia `console.error`, então o efeito visível era quatro
 * seletores **vazios**, sem nada explicando. Ficou assim desde 01/10.
 *
 * ## De onde as listas saem agora
 *
 * Do servidor, por `/filters/available/fields` — a mesma resposta que alimenta
 * as condições do filtro salvo, em uma consulta pequena. É de lá que o Canal
 * passou a sair em 11/10; agora os outros vêm do mesmo lugar.
 *
 * Os chamados carregados entram como **acréscimo**, e só quando a tela já os
 * tem em mãos (`ClientTicketsPage` passa os dele). Nunca como motivo para
 * buscar nada.
 */

const naoVazio = (valor) => typeof valor === 'string' && valor.trim() !== '';

const valorDaOpcao = (opcao) => {
    if (opcao === null || opcao === undefined) return null;
    if (typeof opcao === 'object') return opcao.value ?? null;
    return opcao;
};

const ordenado = (valores) =>
    [...new Set(valores.filter(naoVazio))].sort((a, b) => a.localeCompare(b, 'pt'));

/** As empresas do cliente de um chamado. */
const empresasDoChamado = (chamado) =>
    (Array.isArray(chamado?.ticket_clients) ? chamado.ticket_clients : [])
        .map((tc) => tc?.company)
        .filter(Boolean);

/**
 * As opções que o servidor declara para um campo de condição.
 *
 * @param {object} campos resposta de `/filters/available/fields`
 * @param {string} nome nome do campo (`channel`, `product`, `representative`, …)
 * @returns {string[]} na ordem em que o servidor mandou
 */
export const opcoesDoCampo = (campos, nome) => {
    const lista = campos && Array.isArray(campos.ticket_fields) ? campos.ticket_fields : [];
    const campo = lista.find((f) => f && f.name === nome);
    const opcoes = campo && Array.isArray(campo.options) ? campo.options : [];

    return opcoes.map(valorDaOpcao).filter(naoVazio);
};

/**
 * Todas as listas de uma vez, ordenadas.
 *
 * Ordenadas porque o seletor não deve mudar de ordem entre uma abertura e
 * outra: a lista do servidor vem na ordem dele, e a derivada dos chamados vem
 * na ordem em que eles calharam de ser carregados.
 *
 * `Contato` sai SÓ dos chamados carregados, e nunca do servidor: são milhares
 * de valores distintos, um por chamado, e uma lista desse tamanho não ajuda
 * ninguém. O campo é um `Autocomplete` com `freeSolo`, então digitar sempre
 * funcionou e o refinamento já compara por TRECHO (`includes`) — as sugestões
 * são um extra onde a tela já tem os chamados em mãos.
 *
 * @param {object|null} camposDoServidor resposta de `/filters/available/fields`
 * @param {string[]|null} categoriasCadastradas resposta de `/ticket-categories`
 * @param {Array|null} chamadosCarregados só quando a tela já os tem
 */
export const opcoesDaBuscaAvancada = (camposDoServidor, categoriasCadastradas, chamadosCarregados) => {
    const chamados = Array.isArray(chamadosCarregados) ? chamadosCarregados : [];
    const cadastradas = Array.isArray(categoriasCadastradas) ? categoriasCadastradas : [];

    const produtos = ordenado([
        ...opcoesDoCampo(camposDoServidor, 'product'),
        ...chamados.flatMap((t) => empresasDoChamado(t).map((c) => c?.product_description)),
    ]);

    return {
        canais: ordenado([
            ...opcoesDoCampo(camposDoServidor, 'channel'),
            ...chamados.map((t) => t?.channel),
        ]),
        categorias: ordenado([
            ...cadastradas,
            ...opcoesDoCampo(camposDoServidor, 'category'),
            ...chamados.map((t) => t?.category),
        ]),
        produtos,
        // A linha sai do servidor E das descrições reunidas acima, pela mesma
        // regra do chip do cliente: "MAX Ouro" e "MAX Ilimitado" dão uma opção
        // só, "Max".
        linhasDeProduto: ordenado([
            ...opcoesDoCampo(camposDoServidor, 'linha_do_produto'),
            ...produtos.map((p) => linhaDoProduto({ product_description: p })),
        ]),
        representantes: ordenado([
            ...opcoesDoCampo(camposDoServidor, 'representative'),
            ...chamados.flatMap((t) => empresasDoChamado(t).map((c) => c?.service_group_description)),
        ]),
        contatos: ordenado(chamados.map((t) => t?.contact)),
    };
};
